import { Loader } from '@/components/shared/loader';
import { getFlags, useFlags } from '@/feature-flags';
import { useAuthStore } from '@/store/authStore';
import { lazy, Suspense, type ReactNode } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  ScrollRestoration,
  type IndexRouteObject,
  type NonIndexRouteObject,
} from 'react-router-dom';
import { getChildPathForRouteObject, ROUTE_PATHS, ROUTES } from '../../config';

interface RouteViewWrapperProps {
  children: ReactNode;
}

interface SuspenseWrapperProps {
  children: ReactNode;
}

export interface AppRouteHandle {
  breadcrumb: string;
}

type AppIndexRoute = Omit<IndexRouteObject, 'handle'> & {
  handle?: AppRouteHandle;
};

type AppNonIndexRouteObject = Omit<NonIndexRouteObject, 'handle' | 'children'> & {
  handle?: AppRouteHandle;
  children?: AppRouteObject[];
};

type AppRouteObject = AppIndexRoute | AppNonIndexRouteObject;

const Header = lazy(() =>
  import('../components/shared/header').then(module => ({ default: module.Header })),
);

const SignInStep = lazy(() =>
  import('../components/signInStep').then(module => ({ default: module.SignInStep })),
);

const ClaimsView = lazy(() =>
  import('../views/claims').then(module => ({ default: module.ClaimsView })),
);

const NewClaimView = lazy(() =>
  import('../views/claims').then(module => ({ default: module.NewClaimView })),
);

const ResumeClaimView = lazy(() =>
  import('../views/claims').then(module => ({ default: module.ResumeClaimView })),
);

const DashboardView = lazy(() =>
  import('../views/dashboard').then(module => ({ default: module.DashboardView })),
);

const ErrorView = lazy(() =>
  import('../views/error').then(module => ({ default: module.ErrorView })),
);

const HelpAndSupportView = lazy(() =>
  import('../views/support').then(module => ({ default: module.HelpAndSupportView })),
);

const ImportantInfoView = lazy(() =>
  import('../views/importantInfo').then(module => ({ default: module.ImportantInfoView })),
);

const NotFoundView = lazy(() =>
  import('../views/notFound').then(module => ({ default: module.NotFoundView })),
);

const PoliciesView = lazy(() =>
  import('../views/policies').then(module => ({ default: module.PoliciesView })),
);

const SignInView = lazy(() =>
  import('../views/signIn').then(module => ({ default: module.SignInView })),
);

const VersionView = lazy(() =>
  import('../views/version').then(module => ({ default: module.VersionView })),
);

const RouteViewWrapper = ({ children }: RouteViewWrapperProps) => (
  <div className="w-full min-h-dvh flex flex-col items-stretch justify-between">
    <Header />
    {children}
    <ScrollRestoration />
  </div>
);

const SuspenseWrapper = ({ children }: SuspenseWrapperProps) => (
  <Suspense fallback={<Loader />}>{children}</Suspense>
);

const SessionWrapper = () => {
  const { isLoading, isInitialized, isAuthenticated } = useAuthStore();
  if (!isInitialized) {
    return null;
  }

  if (!isLoading && !isAuthenticated) {
    return <Navigate to={ROUTE_PATHS[ROUTES.SIGN_IN]} replace />;
  }

  return (
    <RouteViewWrapper>
      <Outlet />
    </RouteViewWrapper>
  );
};

const ErrorViewElement = () => (
  <RouteViewWrapper>
    <SuspenseWrapper>
      <ErrorView />
    </SuspenseWrapper>
  </RouteViewWrapper>
);

const NotFoundViewElement = () => (
  <RouteViewWrapper>
    <SuspenseWrapper>
      <NotFoundView />
    </SuspenseWrapper>
  </RouteViewWrapper>
);

const VersionViewElement = () => (
  <RouteViewWrapper>
    <SuspenseWrapper>
      <VersionView />
    </SuspenseWrapper>
  </RouteViewWrapper>
);

const ImportantInfoViewElement = () => {
  const { hasImportantInformationPage } = useFlags();

  return (
    <SuspenseWrapper>
      {hasImportantInformationPage ? <ImportantInfoView /> : <NotFoundView />}
    </SuspenseWrapper>
  );
};

function getDefaulRoutes(): AppRouteObject[] {
  const { canMakeNewClaimInPortal } = getFlags();

  return [
    {
      path: ROUTE_PATHS[ROUTES.DASHBOARD],
      element: <SessionWrapper />,
      handle: {
        breadcrumb: 'Home',
      },
      children: [
        {
          index: true,
          element: (
            <SuspenseWrapper>
              <DashboardView />
            </SuspenseWrapper>
          ),
        },
        {
          path: ROUTE_PATHS[ROUTES.CLAIMS],
          handle: {
            breadcrumb: 'Claims',
          },
          children: [
            {
              index: true,
              element: (
                <SuspenseWrapper>
                  <ClaimsView />
                </SuspenseWrapper>
              ),
            },
            {
              path: ROUTE_PATHS[ROUTES.NEW_CLAIM],
              element: (
                <SuspenseWrapper>
                  {canMakeNewClaimInPortal ? <NewClaimView /> : <NotFoundView />}
                </SuspenseWrapper>
              ),
              handle: {
                breadcrumb: 'Make a claim',
              },
            },
            {
              path: ROUTE_PATHS[ROUTES.RESUME_CLAIM],
              element: (
                <SuspenseWrapper>
                  {canMakeNewClaimInPortal ? <ResumeClaimView /> : <NotFoundView />}
                </SuspenseWrapper>
              ),
              handle: {
                breadcrumb: 'Resume claim',
              },
            },
          ],
        },
        {
          path: ROUTE_PATHS[ROUTES.POLICIES],
          element: (
            <SuspenseWrapper>
              <PoliciesView />
            </SuspenseWrapper>
          ),
        },
        {
          path: ROUTE_PATHS[ROUTES.SUPPORT],
          element: (
            <SuspenseWrapper>
              <HelpAndSupportView />
            </SuspenseWrapper>
          ),
        },
        {
          path: ROUTE_PATHS[ROUTES.IMPORTANT_INFO],
          element: <ImportantInfoViewElement />,
        },
      ],
    },
    {
      path: ROUTE_PATHS[ROUTES.SIGN_IN],
      element: (
        <SuspenseWrapper>
          <SignInView />
        </SuspenseWrapper>
      ),
      children: [
        {
          index: true,
          element: (
            <SuspenseWrapper>
              <SignInStep step="email" />
            </SuspenseWrapper>
          ),
        },
        {
          path: getChildPathForRouteObject(
            ROUTE_PATHS[ROUTES.SIGN_IN_EMAIL],
            ROUTE_PATHS[ROUTES.SIGN_IN],
          ),
          element: (
            <SuspenseWrapper>
              <SignInStep step="email" />
            </SuspenseWrapper>
          ),
        },
        {
          path: getChildPathForRouteObject(
            ROUTE_PATHS[ROUTES.SIGN_IN_DETAILS],
            ROUTE_PATHS[ROUTES.SIGN_IN],
          ),
          element: (
            <SuspenseWrapper>
              <SignInStep step="registration" />
            </SuspenseWrapper>
          ),
        },
        {
          path: getChildPathForRouteObject(
            ROUTE_PATHS[ROUTES.SIGN_IN_OTP],
            ROUTE_PATHS[ROUTES.SIGN_IN],
          ),
          element: (
            <SuspenseWrapper>
              <SignInStep step="otp" />
            </SuspenseWrapper>
          ),
        },
        {
          path: getChildPathForRouteObject(
            ROUTE_PATHS[ROUTES.SIGN_IN_ERROR],
            ROUTE_PATHS[ROUTES.SIGN_IN],
          ),
          element: (
            <SuspenseWrapper>
              <SignInStep step="error" />
            </SuspenseWrapper>
          ),
        },
      ],
    },
    {
      path: ROUTE_PATHS[ROUTES.NOTFOUND],
      element: <NotFoundViewElement />,
    },
    {
      path: ROUTE_PATHS[ROUTES.ERROR],
      element: <ErrorViewElement />,
    },
    {
      path: ROUTE_PATHS[ROUTES.VERSION],
      element: <VersionViewElement />,
    },
    {
      path: '*',
      element: <NotFoundViewElement />,
    },
  ];
}

function getRoutesForError(): AppRouteObject[] {
  return [
    {
      path: '*',
      element: <ErrorViewElement />,
    },
  ];
}

const routerDefault = createBrowserRouter(
  getDefaulRoutes().map(route => ({ ...route, errorElement: <ErrorViewElement /> })),
);

const routerForError = createBrowserRouter(
  getRoutesForError().map(route => ({ ...route, errorElement: <ErrorViewElement /> })),
);

export { routerDefault, routerForError };
